/**
 * RP4 — Per-frame hooks extracted from `Scene3DStage.tsx` SceneContent.
 *
 * Two distinct passes both gated on R3F's demand frameloop:
 *
 *   1. **Resolution sync** — pushes the current canvas pixel resolution
 *      into every LineMaterial uniform (line buckets + SHX text). Must
 *      run on every requested frame because canvas resize is the only
 *      way to change the size and we don't have a dedicated observer.
 *
 *   2. **LOD viewport-cull** — re-classifies entities into full /
 *      bbox-line / point / skip buckets when the camera moved past
 *      the threshold (1 px pan or 0.5 % zoom delta). Bails out cheaply
 *      on identical frames so idle cost stays at zero.
 *
 * # Why a dedicated component
 *
 * Both passes need the R3F context (`useThree` + `useFrame`); putting
 * them in a child component lets the orchestrator pass the refs
 * through props rather than carrying a large closure.
 *
 * No behavior changes — pure code move from `Scene3DStage.tsx` lines
 * 2293-2321 (resolution) and 2352-2402 (LOD).
 */

import * as React from "react";
import * as THREE from "three";
import { useThree, useFrame } from "@react-three/fiber";

import type { BatchedLayer, MeshKeyId } from "../batchedLayer";
import type { RenderableEntity, RendererHandle } from "../types";
import {
  buildPointsCloud,
  computeVisibleSet,
  disposePointsCloud,
  shouldRecomputeVisibleSet,
  type CameraSnapshot,
  type LodEntityState,
  type VisibleEntityGroups,
} from "../lod";

import type {
  BucketState,
  EntityLiftRequest,
  EntityRender,
} from "./SceneRefsTypes";
import { retryPendingTextRuns } from "./pendingTextRetry";
import { collectViewportPointProxies } from "./viewportPointProxies";
import { viewportPointProxyUserData } from "../rendering/viewportPointProxyStamp";
import type { SpaceLayerCensusCache } from "./spaceLayerCensusCache";
import {
  buildSingleBucketMesh,
  deriveBucketSpaceClass,
  upsertEntityRender,
} from "./sceneBulkLoadHelpers";
import { collectRenderSatellites } from "./renderSatellites";
import { applyEntityRenderVisibility } from "./entityRenderVisibility";
import { restampHighlightState } from "./SelectionTextureSync";
import {
  buildPerSegmentLtScales,
  buildPerSegmentSlotIndices,
} from "../rendering/perSegmentLtScale";
import { detectSelectionStateCompat } from "../rendering/selectionStateTexture";
import { applyLayerVisibilityToHandle } from "../state/layerVisibility";
import type { ViewportLiftRequests } from "../viewports/viewportLiftRequests";
import type {
  LinetypeResolutionMap,
  LayerLinetypeMap,
} from "../rendering/linetypeResolve";
import type { LinetypeSysvarsRefValue } from "./useSceneContentRefs";

/**
 * Project `EntityRender.lodState` entries into a flat
 * `Map<string, LodEntityState>` that `computeVisibleSet` can mutate.
 * Opt 8 — the hysteresis state logically belongs to each entity's
 * EntityRender entry (per `SceneRefsTypes`), so we sync the projection
 * back into the consolidated Map after `computeVisibleSet` returns
 * (any removed ids drop their `lodState` field, new ids gain one).
 *
 * Cost: O(n_entities_with_lodState) per camera-changed frame. The
 * hysteresis set scales with the visible-set size, which is
 * upper-bounded by the spatial intersect.
 */
function buildLodProjection(
  entityRenders: Map<string, EntityRender>,
): Map<string, LodEntityState> {
  const out = new Map<string, LodEntityState>();
  for (const [id, render] of entityRenders) {
    if (render.lodState) out.set(id, render.lodState);
  }
  return out;
}

/**
 * After `computeVisibleSet` mutates the projection, write the updated
 * `lodState` field back onto the corresponding EntityRender entry,
 * and clear `lodState` on EntityRender entries that the projection
 * dropped (entities that left the visible set). Preserves any other
 * fields (fillMesh, textObject, textureSlot) on each entry.
 */
function syncLodProjection(
  entityRenders: Map<string, EntityRender>,
  projection: Map<string, LodEntityState>,
): void {
  // Write back: every projection entry → EntityRender.lodState.
  for (const [id, state] of projection) {
    const existing = entityRenders.get(id);
    if (existing) {
      existing.lodState = state;
    } else {
      entityRenders.set(id, { lodState: state });
    }
  }
  // Evict: any EntityRender that had lodState but is no longer in the
  // projection (visibility-set churn) drops its lodState field. We
  // leave the EntityRender entry itself in place so other fields
  // (fillMesh / textObject / textureSlot) survive.
  //
  // `delete` (not `= undefined`) honours `exactOptionalPropertyTypes`:
  // EntityRender declares `lodState?: LodEntityState` (no `| undefined`
  // suffix), so assigning `undefined` would be a type error. Removing
  // the key entirely keeps the field optional + absent.
  for (const [id, render] of entityRenders) {
    if (render.lodState && !projection.has(id)) {
      delete render.lodState;
    }
  }
}

interface FrameLoopsProps {
  /**
   * Opt 8 — consolidated per-bucket Map. The per-frame resolution
   * sync reads `bucket.material` from each entry.
   */
  bucketsRef: React.MutableRefObject<Map<MeshKeyId, BucketState>>;
  /**
   * Opt 8 — consolidated per-entity Map. The per-frame resolution
   * sync walks `render.textObject` for SHX text materials and the
   * LOD viewport-cull pass reads `render.lodState` for hysteresis
   * state.
   */
  entityRendersRef: React.MutableRefObject<Map<string, EntityRender>>;
  handleRef: React.MutableRefObject<RendererHandle | null>;
  prevCameraSnapshotRef: React.MutableRefObject<CameraSnapshot | null>;
  lodPointsCloudRef: React.MutableRefObject<THREE.Points | null>;
  /**
   * Codex PR #784 round-12 P2 (`scene/FrameLoops.tsx:1097`) — the ATTRIBUTED
   * point-proxy clouds, one per (requesting-viewport set, layer) group
   * (`scene/viewportPointProxies.ts`). Held separately from
   * {@link lodPointsCloudRef} because the main camera's cloud is unstamped and
   * unscoped, and these are neither: each is masked to the viewports that asked
   * for its dots and pre-hidden on the bare sheet. Attached at the SCENE ROOT
   * like every other emitter, so `rendering/viewportTraversalCaches.ts` sees
   * them through its root-topology key.
   */
  lodPointProxyCloudsRef: React.MutableRefObject<THREE.Points[]>;
  lodGroupsRef: React.MutableRefObject<VisibleEntityGroups>;
  /**
   * Codex P3 follow-up / Item C (2026-05-19) — per-frame viewport-cull
   * re-lift tracking. The viewport-cull pass below diffs the new
   * `computeVisibleSet` output against this Set: ids in groups.full /
   * groups.bboxLine NOT in this Set get lifted via
   * `entityResolverRef` and added to `BatchedLayer`; ids in this Set
   * NOT in any of groups.full / groups.bboxLine / groups.point get
   * removed. The pass then writes the new lifted-set back into
   * `liftedEntityIdsRef.current` for the next frame's diff.
   */
  liftedEntityIdsRef: React.MutableRefObject<Set<string>>;
  /**
   * S9b — parent id (MLEADER with block content, or a cache-direct INSERT
   * `block-insert` wrapper) → its render-only satellite children
   * (`renderSatellites.ts`). The viewport-cull pass reads / mutates this so the
   * satellites are added / removed in lockstep with the parent: removed
   * alongside the parent when it is culled, re-expanded + re-added when the
   * parent is re-lifted. The children are absent from the spatial index (the
   * parent is the single pickable entity), so they can never appear in a
   * `computeVisibleSet` result and must not be tracked in `liftedEntityIdsRef`.
   */
  multileaderBlockChildrenRef: React.MutableRefObject<Map<string, RenderableEntity[]>>;
  /**
   * Codex PR #784 round 3 P2 — the paper-space orchestrator's model / paper
   * layer census (`scene/spaceLayerCensusCache.ts`, owned by `Scene3DStage`,
   * adopted through `useSceneContentRefs`). Pass 2 UNIONS a relifted parent's
   * satellite layer chains into it (`addSatellites`) instead of bumping the
   * satellite-map revision, which would recompute the census over the whole
   * spatial index on every relift frame — see that module's doc for why a
   * monotonic union is both cheaper and safe.
   */
  spaceLayerCensusRef: React.MutableRefObject<SpaceLayerCensusCache>;
  /**
   * S9b (Codex round-8 P2 — "Reapply highlight state after satellite relift") —
   * live snapshot of the App's current `selectedEntityIds`. The relift path
   * rebuilds a block-content MLEADER's satellite slots with FRESH instance
   * indices; the standalone selection-sync effect only re-runs on a
   * `selectedEntityIds` change, so without re-applying here a callout that was
   * selected BEFORE the pan/zoom relift stops highlighting its block content.
   * Read at frame time so the re-apply uses the current selection.
   */
  selectedEntityIdsRef: React.MutableRefObject<readonly string[]>;
  /**
   * Codex P3 follow-up / Item C (2026-05-19) — on-demand resolver
   * the viewport-cull pass uses to materialise a newly-visible
   * entity that bulk-load dropped at LOD pre-classify time.
   *
   * Codex PR #784 round-8 P2 — the resolver OWNS the whole first-time lift:
   * besides returning the renderable it publishes the parent + its
   * render-only satellites into the bulk-load pass's `renderableById` (the
   * spatial index's resolver map) and emits their direct fill / arrow / glyph
   * / array meshes. Pass 2 therefore treats a never-lifted entity and a
   * lifted-then-culled one identically. Wired in
   * `SceneBulkLoad.ts finishSceneBulkLoad`, which is the only scope holding
   * that pass's scene + registries.
   *
   * Codex PR #784 round-11 — the optional `request` says WHICH camera the id is
   * resolved for: `emitScale` (the largest requesting viewport's model scale)
   * is the scale its direct meshes must be built at, and `proxyOnly` asks for
   * the cheap non-expanding stub instead of a lift. See
   * {@link EntityLiftRequest}.
   */
  entityResolverRef: React.MutableRefObject<
    | ((id: string, request?: EntityLiftRequest) => RenderableEntity | null)
    | null
  >;
  /**
   * Codex P3 follow-up / Item C — BatchedLayer the per-frame pass
   * mutates via `addEntity` / `removeEntity` on diff transitions.
   */
  batchedLayerRef: React.MutableRefObject<BatchedLayer | null>;
  /**
   * Codex PR #784 round-9 P1 (`lod/lodPolicy.ts:225`) — ids the ACTIVE
   * PAPER-SPACE VIEWPORTS need materialised, measured at each viewport's OWN
   * model scale (`viewports/viewportLiftRequests.ts`, published by
   * `Scene3DStage`'s viewport orchestrator).
   *
   * The pass below classifies the spatial index against the MAIN camera only.
   * A viewport is a second window onto the same model at a different scale, so
   * an INSERT that is sub-pixel under the model-wide fit — and therefore
   * dropped by the bulk-load LOD pre-classify — can be large inside one. This
   * set is UNIONED into the newly-visible diff so those ids take the SAME
   * on-demand lift (`entityResolverRef` → publish + `emitReliftDirectMeshes` →
   * `addEntity`) the main camera drives; nothing here is a second lift path.
   *
   * It is also what stops Pass 1 from culling a viewport's geometry: the ids
   * are in the frame's visible set, so the removal walk leaves them alone even
   * while the layout camera looks at bare paper.
   *
   * `version` (not the Set's contents) is the change signal — a bump makes this
   * pass run even when the camera did not move; an unchanged version leaves the
   * camera-delta early-out in charge. Absent for callers without a viewport
   * pass (model space, unit tests).
   */
  viewportLiftRequestsRef?: React.MutableRefObject<ViewportLiftRequests>;
  /**
   * FIX-3 (Codex P2) — per-pass LTYPE resolution map published by
   * `runSceneBulkLoad`. The relift path reads it to build a NEW linetype
   * bucket key (same dash fidelity as the bulk-load path).
   */
  linetypeResolutionRef: React.MutableRefObject<LinetypeResolutionMap>;
  /**
   * FIX-1 (Codex P2) — per-pass per-layer LTYPE map published by
   * `runSceneBulkLoad`. The relift path passes it so a `ByLayer` bucket it
   * materialises on demand resolves through the layer's assigned linetype.
   */
  layerLinetypeMapRef: React.MutableRefObject<LayerLinetypeMap>;
  /**
   * PR-LT3 variant A (A2) — per-pass GLOBAL dash-scale sysvars published by
   * `runSceneBulkLoad`. The relift path passes it so a bucket it materialises
   * on demand gets the same `dashScale` as the bulk-load path.
   */
  linetypeSysvarsRef: React.MutableRefObject<LinetypeSysvarsRefValue>;
  /**
   * FIX-2 (Codex P2) — current derived hidden-layer set (Off / Frozen),
   * maintained by `applyAndMirrorLayerVisibility`. The relift path applies
   * it to a freshly-built bucket so a bucket on a currently-hidden layer is
   * created hidden (not visible until the layer table next mutates).
   */
  hiddenLayersRef: React.MutableRefObject<Set<string>>;
  /**
   * FIX-2 (Codex P2) — current derived locked-layer set, maintained by
   * `applyAndMirrorLayerVisibility`. Applied to a relift-built bucket so a
   * bucket on a locked layer is created with the LAYLOCKFADECTL fade.
   */
  lockedLayersRef: React.MutableRefObject<Set<string>>;
}

/**
 * Component child of `<Canvas>` that hosts both useFrame loops.
 * Returns `null`. Mounted as a sibling of SceneContent inside
 * `<Canvas>` so it can read R3F's scene + camera + size.
 */
export function FrameLoops(props: FrameLoopsProps): null {
  const {
    bucketsRef,
    entityRendersRef,
    handleRef,
    prevCameraSnapshotRef,
    lodPointsCloudRef,
    lodPointProxyCloudsRef,
    lodGroupsRef,
    liftedEntityIdsRef,
    multileaderBlockChildrenRef,
    spaceLayerCensusRef,
    selectedEntityIdsRef,
    entityResolverRef,
    batchedLayerRef,
    viewportLiftRequestsRef,
    linetypeResolutionRef,
    layerLinetypeMapRef,
    linetypeSysvarsRef,
    hiddenLayersRef,
    lockedLayersRef,
  } = props;
  const { scene, gl, size, camera } = useThree((state) => ({
    scene: state.scene,
    gl: state.gl,
    size: state.size,
    camera: state.camera,
  }));
  /**
   * Codex PR #784 round-9 P1 — last `ViewportLiftRequests.version` this pass
   * acted on. A bump is an independent reason to re-run the visible-set pass
   * (the viewports moved / rescaled while the layout camera stood still);
   * equality leaves the camera-delta early-out in charge, so a static layout
   * still costs nothing per frame. `-1` (no real version) forces the first
   * published set through even when it is version 0.
   */
  const lastViewportLiftVersionRef = React.useRef(-1);

  // LineMaterial requires viewport resolution in pixels for sub-pixel
  // line widths. Re-pushing on every frame is cheap (uniform write
  // only) and bullet-proofs us against canvas resize without a
  // dedicated resize observer. PR-B's camera plumbing will consolidate
  // this with the per-frame zoom-aware linewidth in PR-I.
  //
  // PR-E5: SHX text meshes also use `LineMaterial`; their materials
  // live on EntityRender.textObject (not on the bucket material), so
  // we walk that field on every EntityRender and duck-type each
  // material's `resolution` uniform before mutating.
  useFrame(() => {
    const w = Math.max(size.width, 1) * gl.getPixelRatio();
    const h = Math.max(size.height, 1) * gl.getPixelRatio();
    // Opt 8 — walk per-bucket materials via the consolidated Map.
    for (const bucket of bucketsRef.current.values()) {
      // `resolution` is a Vector2 uniform; mutating in place avoids
      // re-binding the uniform location.
      bucket.material.resolution.set(w, h);
    }
    // Duck-type LineMaterial `resolution` sync for one text Object3D. SHX text
    // meshes use `LineMaterial` (whose `resolution` Vector2 must track canvas
    // size); instanced SDF quads have no such uniform, so this is zero-cost for
    // them. Shared by the own-`textObject` walk and the S9b satellite-label walk.
    const syncTextResolution = (textObj: THREE.Object3D): void => {
      const m = (textObj as THREE.Mesh).material as
        | THREE.Material
        | THREE.Material[]
        | undefined;
      if (!m) return;
      const candidates = Array.isArray(m) ? m : [m];
      for (const c of candidates) {
        const res = (c as unknown as { resolution?: THREE.Vector2 })
          .resolution;
        if (res && typeof res.set === "function") {
          res.set(w, h);
        }
      }
    };
    // Opt 8 — walk per-entity text objects via the consolidated Map.
    for (const render of entityRendersRef.current.values()) {
      if (render.textObject) syncTextResolution(render.textObject);
      // S9b — synthesised DIMENSION / MULTILEADER labels ride on the parent
      // render's satellite list; an SHX label there also needs its LineMaterial
      // resolution kept in sync (previously reached via its own `textObject`).
      if (render.satelliteTextObjects) {
        for (const obj of render.satelliteTextObjects) syncTextResolution(obj);
      }
    }
  });

  // PR-J — viewport culling + LOD bucket refresh.
  //
  // # Why a separate useFrame
  //
  // The resolution-uniform pass above runs unconditionally on every
  // requested frame. The LOD pass only does work when the camera moved
  // by more than the threshold (`shouldRecomputeVisibleSet`); that
  // keeps idle frames at zero CPU cost.
  //
  // # Pipeline shape
  //
  // 1. Snapshot current camera (zoom + position).
  // 2. Compare to previous snapshot — bail out if delta < 1 px pan or
  //    < 0.5 % zoom.
  // 3. Compute viewport rect in offset-world space from the
  //    OrthographicCamera frustum.
  // 4. `computeVisibleSet` → spatial.intersect + LOD partition with
  //    hysteresis state.
  // 5. Rebuild the `THREE.Points` cloud from the "point" bucket.
  //
  // # Why not modify existing buckets
  //
  // The baseline BatchedLayer line meshes already render every entity.
  // PR-J's points cloud is *additive*: zoomed-out sub-2-px entities
  // get a visible dot in the cloud while the line bucket continues to
  // render their (now sub-pixel) line — the browser's anti-aliasing
  // collapses both to the same on-screen pixel. A follow-up PR (post-
  // PR-K) can hide the line bucket for "point" / "skip" entities once
  // PR-I's per-entity selection-state texture grows a visibility byte.
  useFrame(() => {
    const handle = handleRef.current;
    if (!handle) return;
    const ortho = camera as THREE.OrthographicCamera;
    if (!ortho.isOrthographicCamera) return;
    const snapshot: CameraSnapshot = {
      zoom: ortho.zoom,
      panX: ortho.position.x,
      panY: ortho.position.y,
    };
    // Codex PR #784 round-9 P1 — the camera delta is no longer the ONLY reason
    // to re-classify. A paper-space viewport can pan / zoom / freeze layers
    // (MSPACE, VPLAYER, a VP move) while the layout camera does not move at
    // all, and the ids it needs are measured at ITS scale, not this camera's.
    // The orchestrator bumps `version` only when the id set actually changes,
    // so an idle layout keeps the zero-work early-out.
    const viewportLift = viewportLiftRequestsRef?.current;
    const viewportLiftVersion = viewportLift ? viewportLift.version : -1;
    const viewportLiftChanged =
      viewportLiftVersion !== lastViewportLiftVersionRef.current;
    if (
      !shouldRecomputeVisibleSet(prevCameraSnapshotRef.current, snapshot) &&
      !viewportLiftChanged
    ) {
      return;
    }
    prevCameraSnapshotRef.current = snapshot;
    lastViewportLiftVersionRef.current = viewportLiftVersion;

    // Build the viewport rect in offset-world. The orthographic
    // frustum half-width in world units is (right - left) / (2 * zoom);
    // pan offset comes from the camera's world position.
    const halfW = (ortho.right - ortho.left) / (2 * ortho.zoom);
    const halfH = (ortho.top - ortho.bottom) / (2 * ortho.zoom);
    const viewport = {
      left: ortho.position.x - halfW,
      right: ortho.position.x + halfW,
      top: ortho.position.y - halfH,
      bottom: ortho.position.y + halfH,
    };

    // Opt 8 — project EntityRender.lodState into a flat hysteresis Map
    // for `computeVisibleSet` (which mutates `prevState` in place via
    // `refreshLodState`), then sync the mutated state back onto each
    // EntityRender entry. Hysteresis crossings survive frame-to-frame
    // because the projection rebuild walks the persisted `lodState`
    // field on every EntityRender.
    const hysteresis = buildLodProjection(entityRendersRef.current);
    const groups = computeVisibleSet({
      spatial: handle.spatialIndex,
      viewport,
      cameraZoom: ortho.zoom,
      prevState: hysteresis,
    });
    syncLodProjection(entityRendersRef.current, hysteresis);
    lodGroupsRef.current = groups;

    // Codex P3 follow-up / Item C (2026-05-19) — per-frame viewport-
    // cull re-lift. Diff the new `(groups.full ∪ groups.bboxLine)`
    // set against `liftedEntityIdsRef.current` (the set of ids
    // currently materialised in BatchedLayer):
    //
    //   newly-visible = present in (full ∪ bboxLine) but NOT in lifted
    //     → lift via `entityResolverRef.current(id)` (returns a fresh
    //       `RenderableEntity` from the cache + buildPreview pipeline)
    //       and `batchedLayer.addEntity(id, renderable)`.
    //
    //   newly-invisible = present in lifted but NOT in any LOD bucket
    //     that the renderer actually emits (`full`, `bboxLine`, or
    //     `point` — `skip` and absence-from-spatial-result share the
    //     "invisible" semantics) → `batchedLayer.removeEntity(id)` so
    //     the GPU bucket slot is zero-filled and the slot is reclaimed
    //     on the next `bulkLoad`.
    //
    // This is the wiring that turns P3's lift-time LOD pre-classify
    // from "drop = permanent until next bulk-load" into "drop =
    // re-add on demand", restoring the AutoCAD-parity behaviour
    // where zoom-in reveals sub-pixel entities the renderer skipped
    // at fit-to-extents.
    //
    // No-op when the resolver / BatchedLayer haven't been wired
    // (initial mount, between bulk-loads). Fragmentation note: each
    // remove/add cycle leaves a zero-filled slot in the bucket
    // vertex buffer; on heavy pan/zoom the bucket can accumulate
    // zombie slots that aren't reclaimed until the next full
    // `bulkLoad`. A follow-up will add periodic compaction on
    // fragmentation threshold; current path accepts the growth as a
    // bounded one-bulk-load cost.
    const batchedLayer = batchedLayerRef.current;
    const resolver = entityResolverRef.current;
    if (batchedLayer && resolver) {
      const liftedSet = liftedEntityIdsRef.current;
      const nextVisibleSet = new Set<string>();
      for (const renderable of groups.full) nextVisibleSet.add(renderable.id);
      for (const renderable of groups.bboxLine) nextVisibleSet.add(renderable.id);
      // groups.point and groups.skip are excluded from the lifted
      // set: point entities render via the dedicated points cloud
      // (built below) and skip entities are intentionally not
      // emitted at all. Keeping them off the BatchedLayer avoids
      // wasting bucket vertex slots on sub-pixel detail.

      // Codex PR #784 round-9 P1 (`lod/lodPolicy.ts:225`) — union in what the
      // active paper-space VIEWPORTS need, measured at each viewport's own
      // model scale (`viewports/viewportLiftRequests.ts`). Two effects, both
      // required and both delivered by this ONE line rather than by a parallel
      // lift path:
      //
      //   * Pass 2 below lifts a viewport-only id through the SAME
      //     `entityResolverRef` seam (publish into `renderableById` + emit the
      //     direct fill / arrow / glyph meshes) and the SAME `addRenderable`,
      //     so a block that is sub-pixel under the model-wide fit but large
      //     inside a viewport gets its bucket slot, its satellites and its
      //     layer chain exactly like a main-camera relift — and is pickable
      //     through the viewport, because the publish is what pick /
      //     window-select / OSNAP read.
      //   * Pass 1 below diffs against this SAME set, so a viewport's geometry
      //     is no longer culled while the layout camera looks at bare paper.
      //
      // Already-lifted ids are skipped by the `liftedSet.has(id)` test in Pass
      // 2 (one `Set.has`, no allocation), which is what keeps the lift one-shot
      // per id on a pass that runs every frame.
      //
      // Codex PR #784 round-10 P1 — what the union may contain is the SAME rule
      // the two loops above express: the ids whose bucket at that viewport's own
      // scale is one the renderer MATERIALISES (`lod/lodPolicy.ts
      // lodBucketIsLifted` — `full` / `bbox-line`). `groups.point` stays out on
      // BOTH paths, so a viewport full of one-pixel INSERTs no longer resolves
      // every one of them into its wrapper + satellite tree here.
      //
      // Codex PR #784 round-11 P1 — `lift` is a MAP now: its value is the
      // largest scale a requesting viewport renders that id at, handed to the
      // resolver below as `EntityLiftRequest.emitScale` so the id's glyphs /
      // patterns are built for the camera that will draw them. The point-band
      // ids (`viewportLift.points`) are deliberately NOT unioned here — they
      // get a dot in the shared cloud instead (round-11 P2, at the bottom of
      // this pass).
      if (viewportLift) {
        for (const id of viewportLift.lift.keys()) nextVisibleSet.add(id);
      }

      // Codex autocheck finding (2026-05-19, PR #92) — after
      // `BatchedLayer.addEntity` / `removeEntity` mutates a bucket's
      // internal `Float32Array` vertex buffer, the corresponding
      // `LineSegments2` mesh's `BufferAttribute` either still points
      // at a stale buffer (addEntity grew the buffer via
      // `ensureBucketCapacity`, reallocating it) or sees zero-filled
      // slots whose GPU upload hasn't happened (removeEntity wrote
      // zeros in place). Either way the displayed mesh is stale —
      // the user sees the previous frame's geometry. Track touched
      // bucket keyIds during the add/remove pass and re-upload each
      // touched bucket via `geometry.setPositions(subarray(0,
      // vertexLength))` after the pass completes.
      const touchedMeshKeys = new Set<MeshKeyId>();

      // Pass 1 — remove ids that are no longer visible at any LOD
      // bucket the BatchedLayer would emit. Walk the lifted Set so
      // a remove also drops the entry from the tracking Set.
      const toRemove: string[] = [];
      for (const id of liftedSet) {
        if (!nextVisibleSet.has(id)) toRemove.push(id);
      }
      for (const id of toRemove) {
        const slot = batchedLayer.getSlot(id);
        if (slot) touchedMeshKeys.add(slot.meshKey);
        batchedLayer.removeEntity(id);
        liftedSet.delete(id);
        // S9b (Codex P2 — "Remove direct MLEADER meshes when culling"): the
        // parent's arrow decorators / synthesised text / fill are DIRECT scene
        // objects (not bucket line slots), so `removeEntity` above does NOT hide
        // them. Mark the entity lod-hidden and re-apply the composite (lod AND
        // layer) visibility so its decorators vanish with the culled line rather
        // than orphaning on screen. Reversed on relift (Pass 2).
        //
        // Codex round-6 P2 ("Record cull state before parent text resolves"): a
        // content-only / leaderless MLEADER whose only content is an ASYNC MTEXT
        // label has NO `EntityRender` yet at cull time (no bucket slot, arrows,
        // or text object registered). `get` would return undefined and record NO
        // hidden state; the async label path then UPSERTS the parent render and
        // applies composite visibility with `lodHidden` unset → the label is
        // added VISIBLE even though the parent was culled. UPSERT here (mirroring
        // the block-child path below) so `lodHidden` is recorded before the mesh
        // exists; the async registration reads it back through the same helper.
        const parentRender = upsertEntityRender(entityRendersRef.current, id);
        parentRender.lodHidden = true;
        applyEntityRenderVisibility(parentRender, hiddenLayersRef.current);
        // S9b — an MLEADER's block-content children / an INSERT wrapper's
        // flattened children are render-only satellites (absent from the
        // spatial index / lifted set). When the parent is culled, remove its
        // children in lockstep so they don't orphan in the bucket after the
        // parent scrolls off-screen or drops below the LOD threshold. Drop
        // the map entry so a later relift re-populates it from the
        // freshly-resolved renderable (Pass 2).
        const blockChildren = multileaderBlockChildrenRef.current.get(id);
        if (blockChildren) {
          for (const child of blockChildren) {
            const childSlot = batchedLayer.getSlot(child.id);
            if (childSlot) touchedMeshKeys.add(childSlot.meshKey);
            batchedLayer.removeEntity(child.id);
            // Codex P2 ("Re-emit non-line block content on relift") twin: a
            // TEXT / HATCH / WIPEOUT block child renders as a DIRECT mesh with
            // NO line-bucket contribution, so `removeEntity` is a bucket no-op
            // for it — hide its direct mesh here so it does not orphan either.
            //
            // Codex round-5 P2 ("Preserve cull state for pending block text"):
            // a TEXT / MTEXT block child builds its mesh ASYNC (font
            // resolution), so at cull time its `EntityRender` may not exist yet.
            // `get` would return undefined and we'd record NO hidden state —
            // then the parent→children map is deleted below, and when the font
            // later resolves the text-registration path (which now reads the
            // composite visibility) would insert the child VISIBLE, orphaned
            // after the parent callout was culled. UPSERT the render so
            // `lodHidden` is recorded EVEN before the mesh exists; the async
            // registration reads it back through `applyEntityRenderVisibility`.
            const childRender = upsertEntityRender(
              entityRendersRef.current,
              child.id,
            );
            childRender.lodHidden = true;
            applyEntityRenderVisibility(childRender, hiddenLayersRef.current);
          }
          multileaderBlockChildrenRef.current.delete(id);
        }
      }

      // Pass 2 — add ids that are newly visible. Skip ids the
      // resolver can't lift (unsupported kind, missing from cache,
      // malformed payload) — they stay invisible until a future
      // frame re-classifies them OR the next bulk-load re-builds
      // from scratch.
      //
      // Codex autocheck Finding 1 (P2) follow-up — track addedEntities
      // so Pass 3 can update the per-entity `textureSlot` mapping
      // (the entity's bucket-local `instanceIndex` changes after
      // remove+add, and downstream selection-state writes need the
      // fresh value).
      //
      // Codex autocheck Finding 4 (P2) — preserve DRAWORDER for the
      // batch of newly-visible entities. `BatchedLayer.addEntity`
      // appends to bucket end (no sort) — without pre-sorting the
      // batch by zOrder, per-frame remove→add transitions emit
      // entities in iteration order (effectively spatial index
      // order). For multi-entity batches this stacks hatches over
      // text, dim lines under fills, etc.
      //
      // Mitigation here: collect newly-visible renderables FIRST,
      // sort by (zOrder asc, id asc) — matching `BatchedLayer.
      // bulkLoad`'s comparator — then call addEntity in that order.
      // This keeps the per-frame batch internally consistent. The
      // GLOBAL DRAWORDER across the existing lifted set + the new
      // batch is NOT fully restored (existing bucket slots retain
      // their bulk-load-time positions); a periodic full bulkLoad
      // to compact + re-sort is the proper follow-up. Document as
      // known limitation.
      const newlyVisibleRenderables: Array<{
        id: string;
        renderable: RenderableEntity;
      }> = [];
      for (const id of nextVisibleSet) {
        if (liftedSet.has(id)) continue;
        // Codex PR #784 round-11 P1 — build this id's meshes for the LARGEST
        // scale that will render it: this camera's zoom, or a magnifying
        // viewport's model scale when the request came from one. `0` means
        // "no viewport asked", which leaves the resolver on the live zoom.
        const renderable = resolver(id, {
          emitScale: viewportLift?.lift.get(id) ?? 0,
        });
        if (!renderable) continue;
        newlyVisibleRenderables.push({ id, renderable });
      }
      newlyVisibleRenderables.sort((a, b) => {
        if (a.renderable.zOrder !== b.renderable.zOrder) {
          return a.renderable.zOrder - b.renderable.zOrder;
        }
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
      });
      const addedEntities: Array<{ id: string; meshKey: MeshKeyId }> = [];
      // FIX-1 (Codex P2) — aggregate the `spaceId`s of the renderables
      // landing in each touched bucket key so a NEWLY-materialised bucket
      // (no BucketState yet) can be tagged with its REAL space class
      // (`deriveBucketSpaceClass`), mirroring the bulk-load classifier in
      // `runSceneBulkLoad`. Without this the new mesh would be hardcoded
      // "model" and a paper-space line's bucket gets pre-hidden on a
      // layout sheet until the next full bulk-load.
      const spaceIdsByMeshKey = new Map<MeshKeyId, Set<string>>();
      // Add one renderable to the BatchedLayer and fold its slot into the
      // touched-key / added-entity / per-key-space bookkeeping. Shared by the
      // parent add and each block-content satellite add (S9b) so both take the
      // identical path (bucket materialisation + selection-slot re-index).
      const addRenderable = (addId: string, r: RenderableEntity): void => {
        batchedLayer.addEntity(addId, r);
        const slot = batchedLayer.getSlot(addId);
        if (slot) {
          touchedMeshKeys.add(slot.meshKey);
          addedEntities.push({ id: addId, meshKey: slot.meshKey });
          let spaceIds = spaceIdsByMeshKey.get(slot.meshKey);
          if (!spaceIds) {
            spaceIds = new Set<string>();
            spaceIdsByMeshKey.set(slot.meshKey, spaceIds);
          }
          spaceIds.add(r.spaceId);
        }
      };
      // S9b — parents relifted THIS frame that carry block content; after the
      // Pass-3 slot refresh their children's fresh slots are re-fanned onto the
      // parent's `satelliteSlots` so selection/hover keeps lighting the callout.
      const reliftedBlockParents: string[] = [];
      for (const { id, renderable } of newlyVisibleRenderables) {
        addRenderable(id, renderable);
        // S9b (Codex P2) — the relifted parent's DIRECT meshes (arrows / synth
        // text / fill) were hidden by a prior cull (Pass 1, `lodHidden`) OR are
        // simply present from bulk-load; clear lod-hidden and re-apply the
        // composite (lod AND layer) visibility so they reappear WITH the leader
        // line — respecting a currently-hidden layer.
        const parentRender = entityRendersRef.current.get(id);
        if (parentRender) {
          parentRender.lodHidden = false;
          applyEntityRenderVisibility(parentRender, hiddenLayersRef.current);
        }
        // S9b — an MLEADER re-lifted by the cull carries its block content in
        // the resolved renderable's `block-content` primitive, and a
        // re-lifted INSERT resolves to its `block-insert` wrapper carrying the
        // flattened children in `childRenderables` — but `addEntity` alone
        // emits ONLY the parent's own line vertices (leader lines; NONE for a
        // wrapper — `block-insert` is out-of-band in the bucket flattener).
        // Re-expand the children here and add them as satellites so the
        // content reappears WITH the parent, and record them so a future cull
        // removes them in lockstep (Pass 1). Empty for a parent without
        // satellites — the common case pays a single kind check.
        //
        // Codex PR #784 round-8 P2 — `resolver(id)` above is not only a lift:
        // for a parent lifted for the FIRST time (a stub the bulk-load LOD
        // pre-classify dropped as sub-pixel, e.g. any small INSERT) it also
        // PUBLISHES the parent + satellites into the pass's `renderableById`
        // (so pick / window-select see real geometry) and EMITS their direct
        // fill / arrow / glyph / array meshes, which no `addEntity` can build
        // — see `SceneBulkLoad.ts finishSceneBulkLoad`. So by the time this
        // loop runs, a first-time child's mesh already exists and the
        // `lodHidden = false` re-show below is all that is left to do,
        // exactly as for a bulk-lifted-then-culled one.
        const blockChildren = collectRenderSatellites(renderable);
        if (blockChildren.length > 0) {
          multileaderBlockChildrenRef.current.set(id, blockChildren);
          // Codex PR #784 round 3 P2 — a parent first lifted HERE (never at
          // bulk-load) brings satellite layers the orchestrator's census has
          // not seen; union them in (O(#children), no index walk — see
          // `spaceLayerCensusCache.ts`) so a child-only layer is never
          // classified "paper-only" inside a viewport. A parent relifted
          // after a cull is a no-op union (its layers are already present).
          spaceLayerCensusRef.current.addSatellites(renderable.spaceId, blockChildren);
          reliftedBlockParents.push(id);
          for (const child of blockChildren) {
            addRenderable(child.id, child);
            // Codex P2 ("Re-emit non-line block content on relift") — a child
            // that renders as a DIRECT mesh (text / hatch / wipeout) is not
            // rebuilt by `addRenderable` (which feeds only line vertices); its
            // mesh survives from bulk-load and was hidden on cull, so re-show it
            // here (composite lod AND layer) rather than leaving it dark.
            const childRender = entityRendersRef.current.get(child.id);
            if (childRender) {
              childRender.lodHidden = false;
              applyEntityRenderVisibility(childRender, hiddenLayersRef.current);
            }
          }
        }
        // Only the parent (a spatial-index entity) enters the lifted Set; the
        // satellite children are tracked via `multileaderBlockChildrenRef`.
        liftedSet.add(id);
      }

      // Pass 3 — re-upload touched buckets so the displayed
      // `LineSegments2` mesh reflects the new BatchedLayer vertex
      // state. Walks the touched-keys Set built during passes 1+2;
      // for each, looks up the BucketState (containing the mesh
      // wrapper) and rebinds the geometry's position attribute via
      // `setPositions` against the bucket's current populated
      // subarray. The mesh's existing attribute is replaced; THREE
      // marks the GPU upload pending. instanceSlotIndices are
      // rebuilt the same way so per-vertex selection-state lookups
      // stay correct after the shuffle.
      //
      // Codex autocheck Finding 2 (P2) follow-up —
      // `LineSegmentsGeometry.setPositions` rebuilds the position
      // attribute but does NOT recompute the per-vertex
      // `lineDistance` attribute that dashed-line materials sample
      // for the dash pattern. Without an explicit
      // `computeLineDistances` call after setPositions, dashed
      // buckets render with stale distances — visible as garbled
      // or missing dashes after relift. Bind unconditionally; on
      // non-dashed buckets the call is a cheap noop that updates a
      // small attribute. Match the bulk-load helper's order
      // (positions → instanceSlotIndex → computeLineDistances).
      //
      // Codex autocheck Finding 3 (P2) follow-up — when
      // `addedEntities.length > selectionTexture.capacity -
      // priorInstanceCount`, the new entities' `instanceIndex` may
      // exceed the texture's pre-allocated slot count. The texture
      // was sized at bulk-load time from
      // `MeshBucket.instanceCount` — relift can grow that count
      // monotonically. Detect overflow per-bucket and reallocate
      // the SelectionStateTexture to fit the live instance count
      // (with headroom). Existing slot bytes are preserved; new
      // slots default to `0` (normal state) per
      // `SelectionStateTexture.resize` semantics.
      if (touchedMeshKeys.size > 0) {
        const liveBuckets = batchedLayer.getBuckets();
        const bucketsByKey = new Map<MeshKeyId, (typeof liveBuckets)[number]>();
        for (const bucket of liveBuckets) {
          bucketsByKey.set(bucket.keyId, bucket);
        }
        // FIX-3 — compat + pixelRatio for any NEW bucket the relift
        // introduces (a newly-visible entity whose linetype produced a
        // bucket key with no `BucketState` yet). Computed lazily on the
        // first new bucket so the common no-new-bucket frame pays nothing.
        let selectionCompat: ReturnType<typeof detectSelectionStateCompat> | null =
          null;
        for (const keyId of touchedMeshKeys) {
          const bucketState = bucketsRef.current.get(keyId);
          const bucket = bucketsByKey.get(keyId);
          if (!bucket) continue;
          if (!bucketState) {
            // FIX-3 (Codex P2) — a NEW linetype bucket key (same
            // layer/color, new linetype) materialised on this relift
            // because `linetype` is now part of the MeshKey. The bulk-
            // load path is the only place that ever built a mesh per
            // bucket; without this the entity stays INVISIBLE until the
            // next full bulk-load. Build + register the bucket here
            // (mirrors `buildBucketMeshes` exactly — same linetype
            // resolution, `LineSegments2`, `computeLineDistances`, scene
            // add) so linetype fidelity is preserved on demand, per
            // newly-visible entity, with no full re-scan.
            if (selectionCompat === null) {
              selectionCompat = detectSelectionStateCompat(gl);
            }
            // `buildSingleBucketMesh` uploads the populated positions +
            // instance slots + computeLineDistances and registers the
            // BucketState into `bucketsRef.current`, so the re-upload
            // steps below are redundant for the freshly built bucket —
            // skip straight to the next key. (Returns null for an empty
            // bucket with nothing to draw yet.)
            //
            // FIX-1 (Codex P2) — classify the new bucket by the REAL space
            // of the renderables that landed in it (collected above),
            // mirroring the bulk-load classifier exactly. Falls back to
            // "model" only for an empty/unclassified set (same default as
            // `runSceneBulkLoad`). A PAPER-space line whose linetype bucket
            // is first materialised by relift must NOT be tagged "model" —
            // the layout orchestrator's pre-hide treats `userData.spaceId`
            // as authoritative and would hide it on the bare sheet.
            const spaceClass = deriveBucketSpaceClass(
              spaceIdsByMeshKey.get(keyId) ?? [],
            );
            const newBucketState = buildSingleBucketMesh({
              scene,
              size,
              bucket,
              buckets: bucketsRef.current,
              resolution: linetypeResolutionRef.current,
              layerLinetypes: layerLinetypeMapRef.current,
              selectionCompat,
              pixelRatio: gl.getPixelRatio(),
              spaceClass,
              // PR-LT3 variant A (A2) — same GLOBAL dash scale as the bulk-load
              // path (published per-pass into `linetypeSysvarsRef`).
              sysvars: linetypeSysvarsRef.current,
            });
            // FIX-2 (Codex P2) — the new BucketState is inserted AFTER
            // `applyAndMirrorLayerVisibility` already ran for this pass, so
            // it never inherited the current hidden/locked layer state.
            // Apply it now (same mutation contract as the bulk-load path's
            // `applyLayerVisibility`) so a bucket on a currently-hidden /
            // frozen layer is created hidden (not flashed visible until the
            // layer table next mutates), and a locked-layer bucket gets the
            // LAYLOCKFADECTL fade. A later layer-unhide then shows it
            // correctly via the standard layer-visibility effect.
            if (newBucketState) {
              applyLayerVisibilityToHandle(
                newBucketState.handle,
                hiddenLayersRef.current,
                lockedLayersRef.current,
              );
            }
            continue;
          }
          const positions = bucket.vertices.subarray(0, bucket.vertexLength);
          // `LineSegmentsGeometry.setPositions` rebuilds the
          // position attribute from the supplied flat array. The
          // call also clears any prior `boundingBox` /
          // `boundingSphere` caches via THREE's internal logic, so
          // raycast / frustum-cull paths stay consistent.
          const geometry = bucketState.mesh.geometry as unknown as {
            setPositions?: (verts: Float32Array | number[]) => void;
            setAttribute?: (name: string, attr: THREE.BufferAttribute) => void;
            setLinetypeScales?: (scales: Float32Array | number[]) => void;
          };
          // `_maxInstanceCount` trap (LineSegmentsGeometry): setPositions must
          // run FIRST — it `delete`s the latched instance cap so the renderer
          // recomputes it from the new (possibly grown) `instanceStart.count`.
          if (geometry.setPositions) geometry.setPositions(positions);
          // fix/selection-highlight-not-applied — re-upload the slot stream as
          // a TRUE per-SEGMENT instanced attribute, mirroring the bulk-load
          // binding. A plain per-vertex attribute is indexed by the quad
          // prototype's vertex id, so half of every relifted segment sampled
          // the wrong state texel (see `buildPerSegmentSlotIndices`).
          if (geometry.setAttribute) {
            geometry.setAttribute(
              "instanceSlotIndex",
              new THREE.InstancedBufferAttribute(
                buildPerSegmentSlotIndices(bucket),
                1,
                false,
              ),
            );
          }
          // PR-LT3 variant A (A2) — FIX (Codex P2, relift re-upload). The
          // relift previously re-uploaded positions + instanceSlotIndex but
          // NOT the per-segment `instanceLtScale`, so a newly-appended
          // CELTSCALE≠1 entity (or a paper-space segment in a mixed bucket)
          // rendered at a stale/default scale until the next full bulk-load.
          // Recompute the per-segment effective scale for the bucket's CURRENT
          // (grown) members — folding the global MS/PS LTSCALE by each
          // segment's own space + CELTSCALE — and re-upload it as a true
          // instanced attribute, exactly mirroring the instanceSlotIndex
          // re-upload above.
          if (geometry.setLinetypeScales) {
            geometry.setLinetypeScales(
              buildPerSegmentLtScales(bucket, linetypeSysvarsRef.current),
            );
          }
          // Codex round-3 autocheck Finding 9 (P2) follow-up —
          // `computeLineDistances` is defined on `LineSegments2` /
          // `Line2` (THREE's fat-line MESH), NOT on the
          // `LineSegmentsGeometry`. Calling it on the geometry was
          // a noop. Targeting the mesh updates the per-segment
          // distance attribute that dashed-line materials sample,
          // so dashed buckets re-render with correct dash patterns
          // after `setPositions`.
          const meshAsLineSegments2 = bucketState.mesh as unknown as {
            computeLineDistances?: () => void;
          };
          if (meshAsLineSegments2.computeLineDistances) {
            meshAsLineSegments2.computeLineDistances();
          }

          // Codex round-3 autocheck Finding 10 (P1) follow-up —
          // selection-texture overflow detection downgraded from
          // "attempt resize via runtime fallback" (the prior
          // speculative `tex.resize(...)` call was a permanent
          // no-op — `SelectionStateTexture` has no `resize`
          // method) to "log warning + accept selection-state
          // dropout on overflow slots". The 2× pre-allocation
          // baked into `sceneBulkLoadHelpers.ts` handles the
          // common relift case (zoom-in → ~2× visible entities);
          // genuine overflow beyond that means the bucket is
          // accumulating instances faster than relift evicts them
          // (= fragmentation, which the deferred follow-up will
          // address via periodic full bulkLoad compaction). On
          // overflow, entities ARE still rendered correctly — only
          // their per-instance selection-state byte addresses
          // outside the texture's slot grid; `setSelectedIds` /
          // `setHoveredId` silently ignore out-of-bounds slot
          // indices in the underlying `update(slot)` (see
          // SelectionStateTexture.ts:139 — `if (slot < 0 || slot
          // >= this.capacity) return`).
          if (
            bucketState.selectionTexture &&
            typeof bucketState.selectionTexture.capacity === "number" &&
            bucketState.selectionTexture.capacity < bucket.instanceCount
          ) {
            console.warn(
              `[FrameLoops] selectionTexture capacity ` +
                `(${bucketState.selectionTexture.capacity}) exceeded by ` +
                `bucket ${keyId} instanceCount (${bucket.instanceCount}). ` +
                `Selection state for slots beyond capacity will be ignored. ` +
                `Follow-up: periodic full bulkLoad compaction or ` +
                `SelectionStateTexture.resize.`,
            );
          }
        }

        // Codex autocheck Finding 1 (P2) — refresh the per-entity
        // `textureSlot` mapping on EntityRender entries for entities
        // that just got fresh `instanceIndex` values via remove+add.
        // Downstream code (`applySelectionToBuckets`,
        // `applyHoverToBuckets`) walks EntityRender.textureSlot to
        // address selection-state bytes; with the prior stale
        // mapping a selection on a re-lifted entity would write to
        // the WRONG instance slot, highlighting a different entity.
        for (const { id, meshKey } of addedEntities) {
          const render = entityRendersRef.current.get(id);
          const slot = batchedLayer.getSlot(id);
          if (!slot) continue;
          if (render) {
            render.textureSlot = {
              keyId: meshKey,
              instanceIndex: slot.instanceIndex,
            };
          } else {
            entityRendersRef.current.set(id, {
              textureSlot: {
                keyId: meshKey,
                instanceIndex: slot.instanceIndex,
              },
            });
          }
        }

        // S9b (Codex P2 — "Mirror selection slots to MLEADER block children") —
        // relift re-indexed the block children's bucket slots (fresh above), so
        // re-fan them onto each relifted parent's `satelliteSlots`; otherwise a
        // post-relift selection of the MLEADER would highlight stale/dropped
        // child slots. Mirrors the bulk-load population in `finishSceneBulkLoad`.
        for (const parentId of reliftedBlockParents) {
          const parentRender = entityRendersRef.current.get(parentId);
          const children = multileaderBlockChildrenRef.current.get(parentId);
          if (!parentRender || !children) continue;
          const satelliteSlots: Array<{
            keyId: MeshKeyId;
            instanceIndex: number;
          }> = [];
          for (const child of children) {
            const childSlot = entityRendersRef.current.get(child.id)?.textureSlot;
            if (childSlot) satelliteSlots.push(childSlot);
          }
          if (satelliteSlots.length > 0) {
            parentRender.satelliteSlots = satelliteSlots;
          } else {
            delete parentRender.satelliteSlots;
          }
        }

        // S9b (Codex round-8 P2 — "Reapply highlight state after satellite
        // relift"): the loop above replaced the relifted parents' satellite
        // slots with fresh instance indices, but the standalone selection-sync
        // effect + hover subscription only fire on a `selectedEntityIds` /
        // hover CHANGE — neither happened during a pure camera relift, so the
        // fresh slots would stay at normal state and a callout selected/hovered
        // BEFORE the pan/zoom would stop highlighting its block content until
        // the user re-selects/re-hovers. Re-stamp the CURRENT selection + hover
        // through the same one-truth fan-out paths so the new slots pick the
        // parent's state back up. Gated on an actual relift of a block-content
        // parent (the common no-MLEADER frame pays nothing).
        if (reliftedBlockParents.length > 0) {
          restampHighlightState({
            entityRenders: entityRendersRef.current,
            buckets: bucketsRef.current,
            selectedEntityIds: selectedEntityIdsRef.current,
          });
        }
      }
    }

    // Codex PR #784 round-11 P1 (`scene/SceneBulkLoad.ts:1391`) — RE-EVALUATE
    // TEXT RUNS THE SCALE GATE SKIPPED.
    //
    // A TEXT / MTEXT run whose PR-V5 height gate produced nothing releases its
    // claim so a later, larger scale can rebuild it — but nothing re-enters the
    // emitter for a run whose parent stays LIFTED, because Pass 2 above skips
    // the resolver for every id already in `liftedSet`. So an INSERT lifted by
    // a large child while a smaller label was still sub-pixel kept that label
    // absent no matter how far the user zoomed in.
    //
    // The pending runs are tracked explicitly (`scene/pendingTextRetry.ts`), so
    // this costs O(skipped runs) and only on a frame that got past the
    // camera-delta / viewport-version early-out above — and O(1) when no
    // pending run's governing scale can have grown. Each run is re-submitted at
    // `max(this camera's zoom, the scale of the viewport that requested its
    // owner)`, the same number the lift itself builds against.
    retryPendingTextRuns({
      entityRenders: entityRendersRef.current,
      mainScale: ortho.zoom,
      viewportScales: viewportLift?.lift,
      maxViewportScale: viewportLift?.maxLiftScale,
    });

    // Rebuild the points cloud whenever the visible set changes. The
    // cloud is small relative to the bucket meshes — even on the П35Р
    // 685k fixture only a fraction of entities fall into the "point"
    // bucket at any one zoom, and rebuilding every camera change keeps
    // the implementation a one-liner.
    //
    // Codex PR #784 round-11 P2 (`viewports/viewportLiftRequests.ts:219`) — a
    // points cloud is also the ONLY thing a viewport's point-band ids ever get.
    // They are measured at the VIEWPORT's scale, so they are typically absent
    // from this camera's `groups.point` (below 0.5 px here, or outside the
    // layout camera's rect altogether) and, being excluded from the lift set by
    // design, they would otherwise have no representation at all inside the
    // viewport that shows them. Resolve each through the NON-EXPANDING proxy —
    // one metadata stub (real bounds + resolved colour), no wrapper, no
    // satellites, no direct meshes — so a viewport full of one-pixel references
    // costs one dot each, exactly what the main camera pays for its own point
    // band. Ids this frame actually lifted are skipped: they are drawn in full.
    //
    // Codex PR #784 round-12 P2 (`scene/FrameLoops.tsx:1097`) — those proxies do
    // NOT go into the main cloud. That cloud is untagged, so no mask can reach
    // it: the union of every viewport's point-band ids painted inside viewports
    // that never asked for them (and froze their layers), and on the bare sheet
    // outside every scissor rect. They are grouped by (requesting-viewport set,
    // layer) instead and each group gets its own small cloud, STAMPED with that
    // attribution (`rendering/viewportPointProxyStamp.ts`) so
    // `applySceneVisibilityMask` keeps it only in the passes that asked and the
    // model-space pre-hide keeps it off the paper. One dot per requested id
    // either way — the grouping partitions the dots, it never duplicates them.
    const proxyGroups = collectViewportPointProxies({
      requests: viewportLift,
      spatial: handle.spatialIndex,
      resolver: entityResolverRef.current,
      lifted: liftedEntityIdsRef.current,
      mainPoints: groups.point,
    });
    disposePointsCloud(lodPointsCloudRef.current);
    if (lodPointsCloudRef.current) {
      scene.remove(lodPointsCloudRef.current);
    }
    const cloud = buildPointsCloud(groups.point);
    if (cloud) {
      cloud.renderOrder = 1; // above hatch (-2/-1) and lines (0)
      scene.add(cloud);
    }
    lodPointsCloudRef.current = cloud;

    const proxyClouds = lodPointProxyCloudsRef.current;
    for (const stale of proxyClouds) {
      scene.remove(stale);
      disposePointsCloud(stale);
    }
    proxyClouds.length = 0;
    for (const group of proxyGroups) {
      const proxyCloud = buildPointsCloud(
        group.entities,
        viewportPointProxyUserData(group),
      );
      if (!proxyCloud) continue;
      proxyCloud.renderOrder = 1; // same band as the main cloud
      scene.add(proxyCloud);
      proxyClouds.push(proxyCloud);
    }
  });

  return null;
}
